import { DashboardCounts, DocumentDashboard } from 'pages/documents/types'
import { Bucket } from './components/VolumeChart'

const empty = (): DashboardCounts => ({
  received: 0,
  completed: 0,
  needsReview: 0,
  failed: 0,
  processing: 0
})

const sum = (a: DashboardCounts, b: DashboardCounts): DashboardCounts => ({
  received: a.received + b.received,
  completed: a.completed + b.completed,
  needsReview: a.needsReview + b.needsReview,
  failed: a.failed + b.failed,
  processing: a.processing + b.processing
})

const utcDate = (iso: string) => new Date(`${iso}T00:00:00Z`)

const fmt = (date: Date, options: Intl.DateTimeFormatOptions) =>
  date.toLocaleDateString(undefined, { timeZone: 'UTC', ...options })

/**
 * Days for short periods, weeks (from Monday) up to ~4 months, then months,
 * so bars stay readable at any period.
 */
export const toBuckets = (daily: DocumentDashboard['daily']): Bucket[] => {
  if (daily.length <= 31) {
    return daily.map((d) => {
      const date = utcDate(d.date)
      return {
        label: fmt(date, { day: 'numeric', month: 'short' }),
        title: fmt(date, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }),
        counts: d.counts
      }
    })
  }

  const weekly = daily.length <= 120
  const groups = new Map<
    string,
    { start: Date; end: Date; counts: DashboardCounts }
  >()
  daily.forEach((d) => {
    const date = utcDate(d.date)
    let key: string
    if (weekly) {
      const start = new Date(date)
      start.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7))
      key = start.toISOString().slice(0, 10)
    } else {
      key = d.date.slice(0, 7)
    }
    const group = groups.get(key) ?? { start: date, end: date, counts: empty() }
    if (date < group.start) group.start = date
    if (date > group.end) group.end = date
    group.counts = sum(group.counts, d.counts)
    groups.set(key, group)
  })

  return Array.from(groups.values()).map(({ start, end, counts }) =>
    weekly
      ? {
          label: fmt(start, { day: 'numeric', month: 'short' }),
          title: `Week of ${fmt(start, { day: 'numeric', month: 'short' })} – ${fmt(end, { day: 'numeric', month: 'short', year: 'numeric' })}`,
          counts
        }
      : {
          label: fmt(start, { month: 'short' }),
          title: fmt(start, { month: 'long', year: 'numeric' }),
          counts
        }
  )
}
